import { Injectable, inject } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { QuestionsV3, QuestionV3ControlTypes } from '../classes/enum';
import { CheckboxQuestionV3 } from '../classes/question-checkbox';
import { DatetimeQuestionV3 } from '../classes/question-datetime';
import { DropdownQuestionV3 } from '../classes/question-dropdown';
import { Eq5QuestionV3 } from '../classes/question-eq5';
import { FieldsBlocQuestionV3 } from '../classes/question-fieldsBloc';
import { FileQuestionV3 } from '../classes/question-file';
import { HiddenQuestionV3 } from '../classes/question-hidden';
import { HtmlQuestionV3 } from '../classes/question-html';
import { ImageQuestionV3 } from '../classes/question-image';
import { MultiDropdownQuestionV3 } from '../classes/question-multiDropdown';
import { PhotoQuestionV3 } from '../classes/question-photo';
import { RadioQuestionV3 } from '../classes/question-radio';
import { RangeQuestionV3 } from '../classes/question-range';
import { SemanticQuestionV3 } from '../classes/question-semantic';
import { SignaturePadV3 } from '../classes/question-signature-pad';
import { TextQuestionV3 } from '../classes/question-text';
import { TextAreaQuestionV3 } from '../classes/question-textarea';
import { ToggleQuestionV3 } from '../classes/question-toggle';
import { DynamicFormInputQuestionV3 } from '../models/dynamic-form-v3.models';
import { NumberRangeQuestionV3 } from './../classes/question-numberRange';
import { SocialSecurityQuestionV3 } from './../classes/question-social-security';
import { VideoQuestionV3 } from '../classes/question-video';
import { ButtonQuestionV3 } from '../classes/question-button';
import { TimeQuestionV3 } from '../classes/question-time';

@Injectable()
export class DynamicFormQuestionControlServiceV3 {
  private fb = inject(UntypedFormBuilder);

  // Return questions array of  descriptions based on the Store
  questionBuilder(questions: DynamicFormInputQuestionV3[]): QuestionsV3[] {
    return questions
      .map(question => {
        switch (question.type) {
          case 'date':
          case 'datetime':
            return new DatetimeQuestionV3(question);

          case 'numberRange':
            return new NumberRangeQuestionV3(question);

          case 'range':
            return new RangeQuestionV3(question);

          case 'radio':
            return new RadioQuestionV3(question);

          case 'checkbox':
            return new CheckboxQuestionV3(question);

          case 'select':
            return new DropdownQuestionV3(question);

          case 'multiDropdown':
            return new MultiDropdownQuestionV3(question);

          case 'textarea':
            return new TextAreaQuestionV3(question);

          case 'semantic':
            return new SemanticQuestionV3(question);

          case 'social_security':
            return new SocialSecurityQuestionV3(question);

          case 'image':
            return new ImageQuestionV3(question);

          case 'eq5':
          case 'eq5-validated':
            return new Eq5QuestionV3(question);

          case 'richtext':
            return new HtmlQuestionV3(question);

          case 'text':
          case 'email':
          case 'number':
          case 'decimal':
          case 'phone':
            return new TextQuestionV3(question);

          case 'photo':
            return new PhotoQuestionV3(question);

          case 'fields-block':
            return new FieldsBlocQuestionV3(question);

          case 'file':
            return new FileQuestionV3(question);

          case 'hidden':
            return new HiddenQuestionV3(question);

          case 'toggle':
            return new ToggleQuestionV3(question);

          case 'signature_pad':
            return new SignaturePadV3(question);

          case 'video':
            return new VideoQuestionV3(question);

          case 'button':
            return new ButtonQuestionV3(question);

          case 'time':
            return new TimeQuestionV3(question);

          default:
            return void 0;
        }
      })
      .filter(question => !!question);
  }

  setFormValue(value, questions: QuestionsV3[]) {
    return questions.reduce((_form, question) => {
      const { key: questionKey, controlType } = question;
      const currentValue = value === null ? '' : value[questionKey];

      switch (controlType) {
        case QuestionV3ControlTypes.RANGE:
          _form[questionKey] = this.parseRangeQuestion(question, currentValue);
          break;

        case QuestionV3ControlTypes.NESTED_GROUP:
          _form[questionKey] = this.parseNestedQuestion(question, currentValue);
          break;

        case QuestionV3ControlTypes.NUMBER_RANGE:
          _form[questionKey] = currentValue === null ? '' : value[questionKey];
          break;

        case QuestionV3ControlTypes.FIELDS_BLOC:
          (question as FieldsBlocQuestionV3).repetitions.map(repetition =>
            repetition.map(field => {
              _form[field.key] = value[field.key];
            })
          );
          break;

        case QuestionV3ControlTypes.DROPDOWN:
          _form[questionKey] =
            currentValue === null || !currentValue[0] ? '' : value[questionKey];
          break;

        default:
          _form[questionKey] = currentValue;
          break;
      }

      return _form;
    }, {});
  }

  // We need to setup default min - max default values
  private parseRangeQuestion(question, value) {
    if (question.dualKnobs === true) {
      const _value = { lower: question.min || 0, upper: question.max };
      if (Array.isArray(value)) {
        _value.lower = value[0];
        _value.upper = value[1];
      }
      return _value;
    }
    return value || question.min;
  }

  private parseNestedQuestion(question, value) {
    const { nestedQuestions } = question;
    return this.parseNestedGroup(nestedQuestions, value);
  }

  private parseNestedGroup(questions, value) {
    return questions.map((question, index) => {
      const { controlType } = question;
      let _value;
      switch (controlType) {
        case QuestionV3ControlTypes.CHECKBOX:
          _value = value === undefined ? false : value[index];
          break;

        case QuestionV3ControlTypes.DATETIME:
          _value = value === undefined ? '' : value[index];
          break;
      }
      return _value;
    });
  }
}
